<template>
  <div v-rect-loading="loading" class="open-api-management-container">
    <TableList
      class="open-api-app-list"
      row-key="id"
      no-action
      :titleI18nKey="`${i18nPrefix}.menu`"
      :list-data="searchedAppList"
      :table-columns="tableColumns"
      :default-sort="defaultSort"
      @create="handleCreate"
    >
      <template #filter>
        <el-select v-model="search.appEnv" class="app-env-filter" clearable :placeholder="$t(`${i18nPrefix}.label.appEnv`)">
          <el-option :label="$t(`${i18nPrefix}.appEnv.prod`)" value="prod"></el-option>
          <el-option :label="$t(`${i18nPrefix}.appEnv.test`)" value="test"></el-option>
        </el-select>
        <el-select v-model="search.companyId" class="company-filter" filterable clearable :placeholder="$t(`${i18nPrefix}.label.company`)">
          <el-option
            v-for="com in appCompanyList"
            :key="com.id"
            :value="com.id"
            :label="`${com.id} - ${com.i18nName}`"
          ></el-option>
        </el-select>
      </template>
      <template #isActive="{ data, row }">
        <el-popconfirm :title="$t('common.message.actionConfirm', { action: $t(`common.btn.${data ? 'disable' : 'enable'}`).toLowerCase() })" @confirm="updateAppIsActive(row)">
          <template #reference>
            <el-switch :value="data"></el-switch>
          </template>
        </el-popconfirm>
      </template>
      <template #securityKey="{ data, row }">
        <span class="security-key">{{ data }}</span>
        <div class="view-btn" @click="onViewBtnClick(row)">
          <i class="btn-icon iconfont" :class="row.showSecurityKey ? 'iconHide1' : 'iconVisible'"></i>
        </div>
      </template>
    </TableList>
    <DialogWrapper
      :show.sync="dialog.show"
      :loading="dialog.loading"
      @save="handleSave"
    >
      <WrappedForm
        ref="form"
        class="open-api-app-info"
        label-width="200px"
        :model="dialog.data"
        :formLines="formLines"
      >
        <template #appId="{ lineName }">
          <el-input v-model="dialog.data.app_id" class="random-btn-input">
            <template #append>
              <el-button class="random-btn" @click="onRandomBtnClick(lineName)">{{ $t(`${i18nPrefix}.label.randomGeneration`) }}</el-button>
            </template>
          </el-input>
        </template>
        <template #securityKey="{ lineName }">
          <el-input v-model="dialog.data.security_key" class="random-btn-input">
            <template #append>
              <el-button class="random-btn" @click="onRandomBtnClick(lineName)">{{ $t(`${i18nPrefix}.label.randomGeneration`) }}</el-button>
            </template>
          </el-input>
        </template>
      </WrappedForm>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import WrappedForm from '@/components/formEditor/wrappedForm.vue'
import { addI18nProperty, getConstantI18nValue, getI18nPropertyValue } from '@/i18n'
import { COMPANY_STATUS } from '@/constant/company'

const AUTH_TYPE = {
  BASIC: 'basic',
  CUSTOM_HTTP_DIGEST_AUTH: 'custom_http_digest'
}

const CODES = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789abcdefghijklmnopqrstuvwxyz'

export default {
  components: {
    TableList,
    DialogWrapper,
    WrappedForm
  },
  data () {
    return {
      i18nPrefix: 'admin.openApi',
      loading: false,
      search: {
        appEnv: null,
        companyId: null
      },
      appList: [],
      defaultSort: { prop: 'is_active', order: 'descending' },
      dialog: {
        show: false,
        loading: false,
        data: {}
      },
      companyLoader: null,
      companyList: [],
      accountList: []
    }
  },
  computed: {
    usedAppIds () {
      const usedIds = _.map(this.appList, appInfo => appInfo.app_id)
      return usedIds
    },
    tableColumns () {
      const columns = [
        {
          width: '80px',
          labelI18nKey: `${this.i18nPrefix}.label.companyId`,
          dataKey: 'company.id'
        },
        {
          width: '100px',
          labelI18nKey: `${this.i18nPrefix}.label.company`,
          dataKey: 'company.i18nName'
        },
        {
          width: '120px',
          labelI18nKey: `${this.i18nPrefix}.label.account`,
          dataKey: 'account.email'
        },
        {
          width: '110px',
          labelI18nKey: `${this.i18nPrefix}.label.appId`,
          dataKey: 'app_id'
        },
        {
          slotName: 'securityKey',
          width: '190px',
          cellClass: 'security-key-container',
          labelI18nKey: `${this.i18nPrefix}.label.securityKey`,
          dataKey: 'displaySecurityKey'
        },
        {
          labelI18nKey: `${this.i18nPrefix}.label.appEnv`,
          width: '96px',
          dataKey: 'appEnv',
          formatter: (data) => this.$t(`${this.i18nPrefix}.appEnv.${data}`),
          sort: { enable: true }
        },
        {
          labelI18nKey: `${this.i18nPrefix}.label.authType`,
          width: '110px',
          dataKey: 'auth_type',
          formatter: (data) => getConstantI18nValue(AUTH_TYPE, data, `${this.i18nPrefix}.authType`),
          sort: { enable: true }
        },
        {
          slotName: 'isActive',
          width: '70px',
          labelI18nKey: `${this.i18nPrefix}.label.isActive`,
          dataKey: 'is_active',
          formatter: (data) => this.$t(`common.label.${data ? 'yes' : 'no'}`),
          sort: { enable: true }
        }
      ]
      return columns
    },
    formLines () {
      const lines = {
        is_active: {
          elType: 'el-switch',
          label: this.$t(`${this.i18nPrefix}.label.isActive`),
          props: {
            class: 'active-switch'
          }
        },
        is_test: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.appEnv`),
          option: {
            data: [
              { label: this.$t(`${this.i18nPrefix}.appEnv.prod`), value: false },
              { label: this.$t(`${this.i18nPrefix}.appEnv.test`), value: true }
            ]
          },
          events: {
            change: this.onAppEnvChange
          }
        },
        auth_type: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.authType`),
          option: {
            data: _.map(AUTH_TYPE, (value, key) => ({ label: this.$t(`${this.i18nPrefix}.authType.${key}`), value }))
          }
        },
        app_id: {
          contentSlot: 'appId',
          label: this.$t(`${this.i18nPrefix}.label.appId`),
          rules: [
            { required: true, message: this.$t('common.message.requiredFormField') },
            {
              trigger: 'change',
              validator: (rule, value, callback) => {
                if (!value) {
                  callback(new Error(this.$t('common.message.requiredFormField')))
                } else if (this.usedAppIds.includes(value)) {
                  callback(new Error(this.$t(`${this.i18nPrefix}.message.duplicatedOpenApiAppId`)))
                } else {
                  callback()
                }
              }
            }
          ]
        },
        security_key: {
          contentSlot: 'securityKey',
          label: this.$t(`${this.i18nPrefix}.label.securityKey`)
        },
        company_id: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.company`),
          option: {
            labelKey: 'i18nName',
            valueKey: 'id',
            data: []
          },
          props: {
            filterable: true
          },
          events: {
            change: this.onCompanyChange
          }
        },
        account_id: {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.account`),
          option: {
            labelKey: 'email',
            valueKey: 'id',
            data: [],
            hasLoad: false,
            loading: false
          },
          props: {
            filterable: true
          }
        }
      }
      _.map(lines, val => {
        if (!val.rules) {
          val.rules = [{ required: true, message: this.$t('common.message.requiredFormField') }]
        }
      })
      return lines
    },
    appCompanyList () {
      const companyList = {}
      _.forEach(this.appList, appInfo => {
        const company = appInfo.company
        if (companyList[company.id]) {
          return
        }
        companyList[company.id] = company
      })
      return Object.values(companyList)
    },
    searchedAppList () {
      const search = this.search
      const result = _.filter(this.appList, appInfo => {
        if (search.companyId && search.companyId !== appInfo.company.id) {
          return false
        } else if (search.appEnv && search.appEnv !== appInfo.appEnv) {
          return false
        }
        return true
      })
      return result
    }
  },
  created () {
    this.refreshData()
  },
  methods: {
    ...mapActions('openApi', ['getOpenApiAppList', 'createOpenApiApp', 'updateOpenApiApp']),
    ...mapActions('superAdmin', ['getSuperAdminCompanyList', 'getHRAccountList']),
    async refreshData () {
      this.loading = true
      try {
        const resp = await this.getOpenApiAppList()
        if (resp) {
          this.appList = _.map(resp, app => this.formatAppInfo(app))
          this.companyLoader = this.loadCompanyList()
        }
      } finally {
        this.loading = false
      }
    },
    async loadCompanyList () {
      if (this.companyLoader) {
        return
      }
      const resp = await this.getSuperAdminCompanyList({ only_root_company: 1, status: COMPANY_STATUS.APPROVED })
      if (resp) {
        this.companyList = _.map(resp.result, com => {
          if (!com.cn_name) {
            com.cn_name = com.name
          }
          if (!com.name) {
            com.name = com.cn_name
          }
          Object.defineProperty(com, 'i18nName', {
            configurable: true,
            enumerable: true,
            get () {
              const i18nName = getI18nPropertyValue(com, 'cn_name', 'name')
              return `${com.id} - ${i18nName || ''}`
            }
          })
          return com
        })
      }
    },
    async loadCompanyAccountList () {
      const option = this.formLines.account_id.option
      option.hasLoad = false
      option.loading = true
      try {
        const resp = await this.getHRAccountList({ company_id: this.dialog.data.company_id, status: '1' })
        if (resp) {
          option.data = resp
        }
      } finally {
        option.hasLoad = true
        option.loading = false
      }
    },
    formatAppInfo (app) {
      const securityKey = app.security_key
      app.encodedSecurityKey = `${securityKey.slice(0, 3)}****${securityKey.slice(-3)}`
      app.showSecurityKey = false
      Object.defineProperty(app, 'displaySecurityKey', {
        configurable: true,
        enumerable: true,
        get () {
          return app.showSecurityKey ? app.security_key : app.encodedSecurityKey
        }
      })
      const company = app.company
      if (!company.cn_name) {
        company.cn_name = company.name
      }
      if (!company.name) {
        company.name = company.cn_name
      }
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')

      const rootCompany = app.root_company
      if (!rootCompany.cn_name) {
        rootCompany.cn_name = rootCompany.name
      }
      if (!rootCompany.name) {
        rootCompany.name = rootCompany.cn_name
      }
      addI18nProperty(rootCompany, 'i18nName', 'cn_name', 'name')
      app.appEnv = app.is_test ? 'test' : 'prod'
      return app
    },
    async handleCreate () {
      this.dialog.loading = true
      this.dialog.data = {
        auth_type: AUTH_TYPE.CUSTOM_HTTP_DIGEST_AUTH,
        app_id: null,
        security_key: null,
        company_id: null,
        account_id: null,
        is_test: false,
        is_active: true
      }
      this.dialog.show = true
      this.$nextTick(() => {
        this.resetCompanyList()
        this.$refs.form.$refs.form.clearValidate()
      })
      try {
        await this.companyLoader
      } finally {
        this.dialog.loading = false
      }
    },
    async handleSave () {
      this.dialog.loading = true
      try {
        const isSuccess = await this.$refs.form.submit()
        if (!isSuccess) {
          return
        }
        const data = this.dialog.data
        const appInfo = {
          app_id: data.app_id,
          security_key: data.security_key,
          auth_type: data.auth_type,
          company_id: data.company_id,
          account_id: data.account_id,
          is_test: data.is_test,
          is_active: data.is_active
        }
        const resp = await this.createOpenApiApp(appInfo)
        if (resp) {
          const app = this.formatAppInfo(resp)
          this.appList.push(app)
        }
      } finally {
        this.dialog.loading = false
      }
      this.dialog.show = false
      this.dialog.data = {}
    },
    resetCompanyList () {
      const usedCompanyIds = _.chain(this.appList).filter(app => app.is_test === this.dialog.data.is_test).map(app => app.company.id).value()
      this.formLines.company_id.option.data = _.filter(this.companyList, com => !usedCompanyIds.includes(com.id))
    },
    onViewBtnClick (appInfo) {
      appInfo.showSecurityKey = !appInfo.showSecurityKey
    },
    onRandomBtnClick (key) {
      const len = key === 'app_id' ? 16 : 30
      const maxIndex = CODES.length - 1
      let str = ''
      _.times(len, () => {
        str += CODES[_.random(maxIndex)]
      })
      this.dialog.data[key] = str
    },
    onAppEnvChange () {
      if (this.dialog.data.app_id) {
        this.$refs.form.$refs.form.validateField('app_id')
      }
      this.resetCompanyList()
    },
    onCompanyChange () {
      this.dialog.data.account_id = null
      this.loadCompanyAccountList()
    },
    async updateAppIsActive (appInfo) {
      this.loading = true
      try {
        const resp = await this.updateOpenApiApp({
          openApiAppId: appInfo.id,
          appInfo: {
            is_active: !appInfo.is_active
          }
        })
        if (resp) {
          appInfo.is_active = resp.is_active
        }
      } finally {
        this.loading = false
      }
    }
  },
  watch: {
    '$i18n.locale' () {
      this.resetCompanyList()
    }
  }
}
</script>

<style lang="scss" scoped>
.open-api-management-container {
  .open-api-app-list {
    ::v-deep {
      .el-table__body {
        .security-key-container {
          .cell {
            display: flex;
          }
        }
      }
    }

    .security-key {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .view-btn {
      display: inline-block;
      margin-left: 10px;
      color: #0096cc;
      cursor: pointer;
    }
  }

  .open-api-app-info {
    ::v-deep {
       &:not(.el-form--label-top) .el-form-item .el-form-item__label .label-inner {
        margin: 0;
        line-height: inherit;
      }

      .active-switch {
        line-height: 32px;
      }

    }

    .random-btn-input {
      ::v-deep {
        .el-input-group__append {
          border-color: #0096cc;
          background: #0096cc;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
